import {
  AnnualMultiTrip,
  Europe,
  EuropeHR,
  EuropeLR,
  Worldwide,
  WorldwideHR,
  WorldwideLR,
} from "@/config/app-constant";
import { PRODUCT_DOMAIN } from "@/constants";
import { MarketingChannelDetails } from "@/types/QuestionPayloadType";
import { Attributes, QuoteData, QuotePayload } from "@/types/quote/quote-data";
import { TripDetails } from "@/types/TripDetails";

import isBCUKEnabledByBrand from "./bcuk-enabled.util";

function selectLoationHookFlash(tripDetails: TripDetails | undefined) {
  let toLocation: string = "";
  if (tripDetails) {
    if (tripDetails?.policyType === AnnualMultiTrip) {
      toLocation = tripDetails?.area ?? "";
    } else {
      toLocation = tripDetails?.toLocation ?? "";
    }
  }

  let selectedLoation: {
    destination: string;
    destinationAdditions: string | undefined;
  } = {
    destination: toLocation,
    destinationAdditions: undefined,
  };

  if (toLocation) {
    if (toLocation === WorldwideHR) {
      selectedLoation = {
        destination: Worldwide,
        destinationAdditions: "true",
      };
    }
    if (toLocation === WorldwideLR) {
      selectedLoation = {
        destination: Worldwide,
        destinationAdditions: "false",
      };
    }
    if (toLocation === EuropeHR) {
      selectedLoation = {
        destination: Europe,
        destinationAdditions: "true",
      };
    }
    if (toLocation === EuropeLR) {
      selectedLoation = {
        destination: Europe,
        destinationAdditions: "false",
      };
    }
  }

  return selectedLoation;
}

const initialPayload: QuoteData = {
  type: "IntermediateData",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    lastVisitedPage: "test",
  },
};

export function modifyPayloadSave(
  payLoadResponse: QuotePayload | null | undefined,
  tripDetailsModified?: TripDetails | undefined,
  marketingChannelDetails?: MarketingChannelDetails,
  isBCUK?: boolean,
  currentSate?: QuoteData
) {
  let payloadModify: QuoteData;
  if (payLoadResponse?.referenceId) {
    payloadModify = {
      type: currentSate?.type ?? "",
      ...currentSate,
      ...payLoadResponse,
      attributes: {
        ...currentSate?.attributes,
        ...payLoadResponse.attributes,
        tripDetails: tripDetailsModified as TripDetails,
        marketingChannelDetails:
          marketingChannelDetails ??
          payLoadResponse?.attributes?.marketingChannelDetails,
        ...{
          partnerCode:
            isBCUK && isBCUKEnabledByBrand
              ? "BCUK"
              : payLoadResponse?.attributes?.partnerCode,
        },
      },
    };
  } else {
    payloadModify = {
      ...currentSate,
      ...initialPayload,
      attributes: {
        ...currentSate?.attributes,
        ...initialPayload.attributes,
        organiser: payLoadResponse?.attributes.organiser,
        tripDetails: tripDetailsModified,
        marketingChannelDetails:
          marketingChannelDetails ??
          payLoadResponse?.attributes?.marketingChannelDetails,
        ...{
          partnerCode:
            isBCUK && isBCUKEnabledByBrand
              ? "BCUK"
              : payLoadResponse?.attributes?.partnerCode,
        },
      } as unknown as Attributes,
    };
  }

  return payloadModify;
}

export function modifyQuestionQuotePayload(
  payLoadResponse: QuotePayload | null | undefined,
  currentSate?: QuoteData
) {
  try {
    const travellerDetails = {
      ...currentSate?.attributes?.travellerDetails,
      medicalTermsAndConditions:
        payLoadResponse?.attributes?.travellerDetails
          ?.medicalTermsAndConditions,
      traveller: payLoadResponse?.attributes?.travellerDetails?.traveller,
      showMoneyHelperMessage:
        payLoadResponse?.attributes?.travellerDetails?.showMoneyHelperMessage,
    };

    const modifiedTripDetails = payLoadResponse?.attributes?.tripDetails;
    if (!modifiedTripDetails?.toLocation) {
      delete modifiedTripDetails?.toLocation;
      delete modifiedTripDetails?.toLocationCountryDetails;
    }

    const quotoEditResponse = {
      type: payLoadResponse?.type ?? "",
      ...currentSate,
      channel: "ONLINE",
      referenceId: payLoadResponse?.referenceId ?? currentSate?.referenceId,
      version: payLoadResponse?.version ?? currentSate?.version,
      attributes: {
        ...currentSate?.attributes,
        domain: PRODUCT_DOMAIN,
        organiser: payLoadResponse?.attributes?.organiser,
        tripDetails: payLoadResponse?.attributes?.tripDetails,
        travellerDetails,
        marketingChannelDetails:
          payLoadResponse?.attributes?.marketingChannelDetails,
        partnerCode: payLoadResponse?.attributes?.partnerCode,
        experimentDetails: payLoadResponse?.attributes?.experimentDetails,
      },
    };

    return quotoEditResponse;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error modifying question quote payload:", error);
    throw error; // Re-throw the error after logging it
  }
}

export interface PageWindow extends Window {
  pageLoadUrls?: string[];
}

export function accessPagePreviousURL(window: any) {
  const extendedWindow =
    typeof window !== "undefined" && (window as PageWindow);
  let userReferrer = "";
  if (extendedWindow) {
    extendedWindow.pageLoadUrls = extendedWindow.pageLoadUrls ?? [];
    userReferrer =
      extendedWindow.pageLoadUrls.length > 1
        ? extendedWindow.pageLoadUrls[extendedWindow.pageLoadUrls.length - 2]
        : "";

    return userReferrer;
  }
}

export function getLastsuccessPage(window: any) {
  const extendedWindow =
    typeof window !== "undefined" && (window as PageWindow);
  let userPage = "";
  if (extendedWindow) {
    extendedWindow.pageLoadUrls = extendedWindow.pageLoadUrls ?? [];
    userPage =
      extendedWindow.pageLoadUrls.length > 1
        ? extendedWindow.pageLoadUrls[extendedWindow.pageLoadUrls.length - 1]
        : "";

    return userPage;
  }
}

export function isLastIndexIncludeSameValue(array: string[], value: string) {
  // Check if the array is empty
  if (array.length === 0) {
    return false;
  }

  // Get the last element of the array
  const lastElement = array[array.length - 1];

  // Compare the last element with the value
  return lastElement === value;
}

export function storageAvailable() {
  try {
    if (typeof window !== "undefined") {
      const storage = window.sessionStorage;
      const x: string = "__storage_test__";
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Error checking storage availability:", e);
    return false;
  }
}

export const getCookieValue = (name: string, cookie: unknown) => {
  return (
    (cookie &&
      (cookie as string)

        .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
        ?.pop()) ??
    ""
  );
};

export default selectLoationHookFlash;

export function capitalizeFirstLetter(name: string) {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}
